/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import {
	AI_API_KEY,
	CLOUD_SERVICES_TOKEN_URL,
	UPLOADCARE_PUBKEY,
	CLOUD_SERVICES_WEBSOCKET_URL,
	LICENSE_KEY
} from '../../constants/configPlaceholders';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { getPluginByClassName } from '../getPluginByClassName/getPluginByClassName';
import { getVariableName } from '../getVariableName/getVariableName';
import type { alignCKEditorPluginToRequiredLanguage } from '../alignCKEditorPluginToRequiredLanguage/alignCKEditorPluginToRequiredLanguage';

export type ConfigUpdateAlert = {
	call: string;
	fn: string;
};

type ExpectedLanguage = Parameters<typeof alignCKEditorPluginToRequiredLanguage>[ 1 ];

export function getConfigUpdateAlert( {
	editorPlugins,
	editorConfig,
	configRef,
	lang = 'js'
}: {
	editorPlugins: EditorPlugins;
	editorConfig: string;
	configRef: string;
	lang?: ExpectedLanguage;
} ): ConfigUpdateAlert {
	const requiredAlerts = getRequiredAlerts( { editorConfig, editorPlugins } );

	if ( !requiredAlerts.length ) {
		return { call: '', fn: '' };
	}

	const functionReference = lang === 'ts' ? '( configUpdateAlert as any)' : 'configUpdateAlert';

	const call = `configUpdateAlert( ${ configRef } );`;

	/* eslint-disable max-len */
	const fn = `
		/**
		 * This function exists to remind you to update the config needed for premium features.
		 * The function can be safely removed. Make sure to also remove call to this function when doing so.
		 */
		function configUpdateAlert( config${ lang === 'ts' ? ': any' : '' } ) {
			if ( ${ functionReference }.configUpdateAlertShown ) {
				return;
			}

			const isModifiedByUser = ( currentValue${ lang === 'ts' ? ': string|undefined' : '' }, forbiddenValue${ lang === 'ts' ? ': string' : '' } ) => {
				if ( currentValue === forbiddenValue ) {
					return false;
				}

				if ( currentValue === undefined ) {
					return false;
				}

				return true;
			}

			const valuesToUpdate = [];

			${ functionReference }.configUpdateAlertShown = true;

			${ requiredAlerts.join( '\n\n' ) }

			if ( valuesToUpdate.length ) {
				window.alert( [
					'Please update the following values in your editor config',
					'to receive full access to Premium Features:',
					'',
					...valuesToUpdate.map( value => \` - \${ value }\` )
				].join( '\\n' ) );
			}
		}
	`;
	/* eslint-enable max-len */

	return { call, fn };
}

type TokenAlertConfiguration = {
	shouldDisplay: boolean;
	configPath: string;
	placeholderValue: string;
	variableName: string;
};

function getRequiredAlerts( {
	editorConfig,
	editorPlugins
}: {
	editorConfig: string;
	editorPlugins: EditorPlugins;
} ): Array<string> {
	const hasPremiumPlugins = editorPlugins
		.map( getPluginByClassName )
		.some( plugin => plugin.premium && plugin.premium.includes( 'licenseKey' ) );

	const tokenAlertConfiguration: Array<TokenAlertConfiguration> = [
		{
			shouldDisplay: hasPremiumPlugins,
			configPath: 'config.licenseKey',
			placeholderValue: LICENSE_KEY,
			variableName: getVariableName( { LICENSE_KEY } )
		},
		{
			shouldDisplay: editorConfig.includes( getVariableName( { AI_API_KEY } ) ),
			configPath: 'config.ai?.openAI?.requestHeaders?.Authorization',
			placeholderValue: 'Bearer ' + AI_API_KEY,
			variableName: getVariableName( { AI_API_KEY } )
		},
		{
			shouldDisplay: editorConfig.includes( getVariableName( { UPLOADCARE_PUBKEY } ) ),
			configPath: 'config.uploadcare?.pubkey',
			placeholderValue: UPLOADCARE_PUBKEY,
			variableName: getVariableName( { UPLOADCARE_PUBKEY } )
		},
		{
			shouldDisplay: editorConfig.includes( getVariableName( { CLOUD_SERVICES_TOKEN_URL } ) ),
			configPath: 'config.cloudServices?.tokenUrl',
			placeholderValue: CLOUD_SERVICES_TOKEN_URL,
			variableName: getVariableName( { CLOUD_SERVICES_TOKEN_URL } )
		},
		{
			shouldDisplay: editorConfig.includes( getVariableName( { CLOUD_SERVICES_WEBSOCKET_URL } ) ),
			configPath: 'config.cloudServices?.webSocketUrl',
			placeholderValue: CLOUD_SERVICES_WEBSOCKET_URL,
			variableName: getVariableName( { CLOUD_SERVICES_WEBSOCKET_URL } )
		}
	];

	const output = tokenAlertConfiguration
		.filter( item => item.shouldDisplay )
		.map( item => {
			return `
			if ( !isModifiedByUser( ${ item.configPath }, '${ item.placeholderValue }') ) {
				valuesToUpdate.push( '${ item.variableName }' );
			}
			`;
		} );

	// By default `config.ai.aws` is an empty object. Let's check it.
	if ( editorPlugins.includes( 'AWSTextAdapter' ) ) {
		output.push( `
			if ( !config.ai?.aws || !Object.keys( config.ai.aws ).length ) {
				valuesToUpdate.push( 'editorConfig.ai.aws' );
			}`
		);
	}

	return output;
}
